import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, LineChart } from "lucide-react";
import { api, errMsg, sourceFor } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell, PageTitle } from "@/components/AppShell";
import { ConnectShop } from "@/components/ConnectShop";
import { Stat } from "@/components/Chips";
import { FixFirst } from "@/components/FixFirst";
import { CatalogTable } from "@/components/CatalogTable";
import { Button } from "@/components/ui/button";

export const downloadBackup = async () => {
  try {
    const res = await api.get("/listings/backup.csv", { responseType: "blob" });
    const name = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "aethertags-backup.csv";
    const url = URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Backup downloaded.");
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const Reconnect = () => (
  <div data-testid="reconnect-banner" className="rounded-2xl border border-rose-300/25 bg-rose-400/10 p-6">
    <p className="font-display text-xl text-rose-200">Reconnect your Etsy shop.</p>
    <p className="mt-1 text-sm text-rose-200">Your Etsy access expired or was revoked. Reconnect to keep your catalog in sync.</p>
    <Button className="mt-4 rounded-full" data-testid="reconnect-etsy-button"
      onClick={() => api.get("/etsy/oauth/start").then((r) => (window.location.href = r.data.url)).catch((e) => toast.error(errMsg(e)))}>
      Reconnect
    </Button>
  </div>
);

const Catalog = ({ shop }) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/listings").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const src = sourceFor(shop);
  if (!data) return <div data-testid="catalog-loading" className="h-64 rounded-2xl shimmer" />;
  const s = data.summary;
  const byId = Object.fromEntries(data.listings.map((l) => [l.id, l]));
  return (
    <>
      <PageTitle eyebrow={shop.kind === "demo" ? "Demo shop" : "Etsy shop"} title={shop.name} testid="catalog-shop-name">
        <Button variant="outline" className="rounded-full" onClick={downloadBackup} data-testid="download-backup-button">
          <Download className="h-4 w-4 mr-1" /> Download backup
        </Button>
        <Button asChild className="rounded-full" data-testid="open-proof-button">
          <Link to="/app/proof"><LineChart className="h-4 w-4 mr-1" /> Proof view</Link>
        </Button>
      </PageTitle>
      {shop.status === "importing" && <p data-testid="import-status" className="mb-6 text-sm text-muted-foreground">Importing your active listings from Etsy…</p>}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat testid="stat-avg-score" label="Avg health score" value={s.avg_score} source="Estimate" hint={`${s.total} listings scored`} />
        <Stat testid="stat-bands" label="Red / yellow / green" value={`${s.red} · ${s.yellow} · ${s.green}`} source="Estimate" hint="<50 · 50–74 · ≥75" />
        <Stat testid="stat-protected" label="Protected bestsellers" value={s.protected} source={src} hint="Sale in the last 60 days" />
        <Stat testid="stat-views" label="Views, last 30 days" value={s.views_30d.toLocaleString()} source={src} hint={`${s.updated} listings updated`} />
      </div>
      <div className="mt-10 grid grid-cols-1 gap-8 xl:grid-cols-[360px_1fr]">
        <FixFirst items={data.fix_first.map((id) => byId[id]).filter(Boolean)} />
        <CatalogTable listings={data.listings} source={src} />
      </div>
    </>
  );
};

export default function Dashboard() {
  const { shop, loadShop, loadConfig } = useAuth();
  const [params] = useSearchParams();
  useEffect(() => {
    const err = params.get("etsy_error");
    if (err === "invite_only") toast.error("Real shop connection is invite-only during the founding beta.");
    else if (err) toast.error("Couldn't connect your Etsy shop. Please try again.");
    if (params.get("connected")) { loadShop(); toast.success("Etsy shop connected. Importing listings…"); }
    loadConfig();
  }, [params, loadShop, loadConfig]);

  return (
    <AppShell>
      {shop === undefined ? (
        <div className="h-64 rounded-2xl shimmer" />
      ) : !shop ? (
        <ConnectShop />
      ) : shop.status === "reconnect_required" ? (
        <Reconnect />
      ) : (
        <Catalog shop={shop} />
      )}
    </AppShell>
  );
}
